import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useController, useFormContext } from 'react-hook-form';

import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import VideoFile from '@mui/icons-material/VideoFile';
import FilePresent from '@mui/icons-material/FilePresent';
import Avatar from '@mui/material/Avatar';

import { VpIcon } from '@vp-ui/components/VpIcon';
import VpButton from '@vp-ui/components/VpButton';

import { useFetchClpUploadPresignedUrl } from '@src/hooks/react-query/useMediaData';
import { useGetSession } from '@src/hooks/useAuthData';
import { useFileUploadData } from '@src/hooks/useFileUploadData';
import { useVpSnackbar } from '@vp-ui/hooks';

import {
  StyledDragnDrop,
  StyledProgressStack,
} from '@vp-ui/components/VpHookFormFileDragAndDrop/styles';
import { getFileSizeLabel } from '@src/utils';
import { Uploader } from '@src/utils/upload';
import config from '@src/config';

const MAX_FILES = 5;
const MAX_SIZE_MEGABYTES = 500;

export const VpHookFormFileDragAndDrop = ({
  name = 'fileInput',
  limit = MAX_FILES,
  sizeLimit = MAX_SIZE_MEGABYTES,
  setCanSubmitForm,
  setFiles,
  files,
}) => {
  const { addSnackbarError } = useVpSnackbar();
  const { mutateAsync: fetchClpUploadPresignedUrl, isLoading: isLoadingPresignedUrl } =
    useFetchClpUploadPresignedUrl();
  const { isLoading: isLoadingAttachment, mutateAsync: uploadAttachment } = useFileUploadData();
  const { data: authData } = useGetSession();

  const [hovering, setHovering] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileCount, setUploadedFileCount] = useState(0);
  const [isLoadingVideoUpload, setIsLoadingVideoUpload] = useState(false);

  const isLoading = isLoadingPresignedUrl || isLoadingAttachment || isLoadingVideoUpload;
  setCanSubmitForm(!isLoading);

  const { getValues } = useFormContext();

  const {
    field: { onChange },
  } = useController({
    name: name,
    rules: { required: true },
    shouldUnregister: true,
  });

  const handleFileUpload = (event) => {
    const files = Object.values(event.target.files);
    handleFileReading(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setHovering(true);
  };

  const handleDrop = (event) => {
    setHovering(false);
    event.preventDefault();
    if (isLoading) {
      return addSnackbarError({
        message: 'Please wait for the previous upload to finish before including another file.',
      });
    }
    const files = Object.values(event.dataTransfer.files);
    handleFileReading(files);
  };

  const getTotalFilesSize = (files) => {
    return files.reduce((previous, current) => previous + current?.size || 0, 0);
  };

  const calculateFileSize = (files) => {
    const currentSize = files[0]?.size;

    const fileInputState = getValues(name);

    const sum = fileInputState?.length ? getTotalFilesSize(files) : currentSize;

    return sum;
  };

  const handleFileReading = (files = []) => {
    try {
      if (files.length > limit || getValues(name)?.length + files.length > limit) {
        return addSnackbarError({ message: `You can only upload up to ${limit} files.` });
      }

      const newTotalFileSize = calculateFileSize(files);

      if (newTotalFileSize > sizeLimit * 1000000) {
        return addSnackbarError({ message: `You can only upload up to ${sizeLimit} MB of files.` });
      }

      setUploadedFileCount(uploadedFileCount + 1);
      setUploadProgress(0);

      files?.forEach(async (file, index) => {
        const isVideo = file.type.startsWith('video/');
        if (isVideo) {
          const mediaSubmission = {
            isRecorded: false,
            userAgent: navigator.userAgent,
            screenResolution: `${window.outerWidth}x${window.outerHeight}`, // fix
            userIp: '',
            mimeType: file.type,
            fileSize: getFileSizeLabel(file.size),
          };

          const { fileKey, mediaSubmissionId } = await fetchClpUploadPresignedUrl(mediaSubmission);
          setIsLoadingVideoUpload(true);

          const uploaderOptions = {
            file,
            baseURL: config.AWS.fileUploadApiGateway,
            chunkSize: 5,
            threadsQuantity: 5,
            useTransferAcceleration: true,
          };
          const uploader = new Uploader(uploaderOptions);

          try {
            await new Promise((resolve, reject) => {
              uploader.onComplete(resolve).onError(reject);
              uploader.start(fileKey);
            });

            const size = file.size;
            const oldValue = getValues(name) || [];
            const newUrls = [
              ...oldValue,
              {
                mediaSubmissionId: mediaSubmissionId,
                size,
              },
            ];
            setFiles(newUrls);
            onChange(newUrls);
            setCanSubmitForm(!isLoading);
            setIsLoadingVideoUpload(false);
          } catch (e) {
            setIsLoadingVideoUpload(false);
            // eslint-disable-next-line no-console
            console.log(e);
          }
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('organizationId', authData.data.fileAuthScope.organizationId);
          const uploadAttachmentResponse = await uploadAttachment({ data: formData });
          const attachmentInfo = uploadAttachmentResponse.data;

          const oldContent = getValues('content') || [];
          const newContent = [
            ...oldContent,
            {
              ...attachmentInfo,
            },
          ];

          onChange(newContent);
          setFiles(newContent);
        }

        setUploadProgress(((index + 1) / files.length) * 100);
      });
    } catch (e) {
      //remove all previous files
      onChange([]);
      setFiles([]);

      // logging error
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  return (
    <Stack gap="5px" width="100%">
      <Typography variant="inputLabelSmall">Imported Content</Typography>
      <StyledDragnDrop
        onDragOver={handleDragOver}
        onDragLeave={() => setHovering(false)}
        onDrop={handleDrop}
        hovering={hovering}
      >
        <input
          type="file"
          id="Content_Drag_Drop_Input_Field"
          accept="image/*, video/*, application/pdf, text/plain, text/html, application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileUpload}
        />
        <VpIcon icon="UploadCloud" />
        <Typography variant="greyDark12Bold" align="center">
          Drag and Drop or{' '}
          <VpButton
            variant="text"
            sx={{ padding: 0, margin: 0, fontSize: 12 }}
            disabled={isLoading || hovering} //add file limit check
            component="label"
            htmlFor="Content_Drag_Drop_Input_Field"
          >
            Choose Files
          </VpButton>
          {` to Upload (Max ${limit} files or ${sizeLimit}MB)`}
        </Typography>
        <Typography variant="caption" align="center">
          Videos (MP4 or MOV preferred), Images (PNG or JPEG) and Documents (PDF, csv, xls and docx)
        </Typography>
        <Box sx={{ display: 'flex' }}>
          {files?.map((f, i) => {
            const isVideo = !!f.mediaSubmissionId;
            return (
              <Box
                key={f.size + i}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Avatar sx={{ margin: '0 5px', backgroundColor: 'vpColors.blueMain' }}>
                  {isVideo ? <VideoFile /> : <FilePresent />}
                </Avatar>
                <Typography variant="caption">{`${isVideo ? 'Video ' : 'File '} ${
                  i + 1
                }`}</Typography>
              </Box>
            );
          })}
        </Box>
        {isLoading && (
          <Box sx={{ display: 'flex' }}>
            <CircularProgress />
          </Box>
        )}
      </StyledDragnDrop>
      <StyledProgressStack>
        <Stack>
          <VpIcon icon="Video" />
          <Typography variant="darkBold14">{`Upload Progress: ${uploadProgress.toFixed(
            0
          )}%`}</Typography>
        </Stack>
        <Typography variant="caption">
          {files?.length
            ? `${getFileSizeLabel(getTotalFilesSize(files))} - ${files.length} ${
                files.length > 1 ? 'files' : 'file'
              } uploaded.`
            : 'No files uploaded.'}{' '}
        </Typography>
        <LinearProgress variant="determinate" value={uploadProgress} />
      </StyledProgressStack>
    </Stack>
  );
};

VpHookFormFileDragAndDrop.propTypes = {
  defaultValue: PropTypes.string,
  name: PropTypes.string,
  limit: PropTypes.number,
  sizeLimit: PropTypes.number,
  setCanSubmitForm: PropTypes.func,
  setFiles: PropTypes.func,
  files: PropTypes.arrayOf(PropTypes.object),
};

export default VpHookFormFileDragAndDrop;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpHookFormFileDragAndDrop/VpHookFormFileDragAndDrop.jsx